import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import {
  API,
  SettingsContextObject,
  ControlState,
  ControlStyleClass,
  ControlValidation,
  getUuidV4,
  Logging,
  Notification,
  NotificationData,
  renderEdit,
  ResponseError,
  settingRejectionLetterUrl,
  settingRejectionReason,
  Setting,
  validateHttpHttpsUri,
  SettingsContext,
  certificateUuidMarker,
  editTokenMarker,
  UserInfo,
  PermissionGroup,
  UserContext,
  getEditPermission,
} from '@vcd-ecm-ui/Common';
import { VtxTooltip } from '@vertexinc/vtx-ui-react-component-library';
import Field from '../../Common/Field/Field';
import Footer from '../../Common/Footer/Footer';
import { FieldItem } from '../../Common/Field/FieldItem';
import RejectionSettingsProps from './RejectionSettingsProps.interface';
import { updateSettingsObject } from '../../Common/Common';
import '../../Common/common-settings.scss';
import '../certificate-settings.scss';

const controlStateEnabled: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const controlStateDisabled: ControlState = {
  editable: false,
  disabled: true,
  readOnly: true,
};

const controlStyle: ControlStyleClass = null;

const originalValuePrefix = 'Rejection Reason';
const fieldLabel = 'Reason';

const RejectionSettings = ({
  gatewayEndPoint,
  settings,
}: RejectionSettingsProps): JSX.Element => {
  const [notification, setNotification] = React.useState(null);
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionSettingsEdit: boolean = getEditPermission(userContext, PermissionGroup.Settings);

  const dispatch = useDispatch();

  const getCertificateRejectionLetterUrl = (): string => {
    Logging.Debug('getCertificateRejectionLetterUrl');
    const filteredSettings: Setting[] = settings.filter((x: Setting) => x.name === settingRejectionLetterUrl);

    if (filteredSettings && filteredSettings.length === 1) {
      return filteredSettings[0].value ? filteredSettings[0].value.toString() : null;
    }

    return null;
  };

  const getCertificateRejectionReasons = (): Setting[] => {
    Logging.Debug('getCertificateRejectionReasons');
    return settings.filter((x: Setting) => x.name === settingRejectionReason);
  };

  const getInitialFields = (): FieldItem[] => {
    const fieldsArray: FieldItem[] = [];

    getCertificateRejectionReasons().forEach((setting: Setting) => {
      const originalValue = `${originalValuePrefix} ${setting.sequenceNumber}`;
      const value = setting.value ? setting.value.toString() : originalValue;

      fieldsArray.push({
        index: setting.sequenceNumber,
        active: setting.activeInd,
        initialActive: setting.activeInd,
        originalValue,
        value,
        initialValue: value,
      });
    });

    return fieldsArray;
  };

  const [fields, setFields] = React.useState(getInitialFields());
  const [initialFields, setInitialFields] = React.useState(getInitialFields());

  const [initialUrl, setInitialUrl] = React.useState(getCertificateRejectionLetterUrl());
  const [url, setUrl] = React.useState(getCertificateRejectionLetterUrl());

  // Field Active was Changed
  const onChangeActive = (index: number, active: boolean): void => {
    const fieldsCopy = _.cloneDeep(fields);

    fieldsCopy[index - 1].active = active;

    setFields(fieldsCopy);
  };

  // Field Value was Changed
  const onChangeValue = (index: number, value: string): void => {
    const fieldsCopy = _.cloneDeep(fields);

    fieldsCopy[index - 1].value = value;

    setFields(fieldsCopy);
  };

  // Render the Rejection Reasons
  const renderRejectionReasons = (): JSX.Element[] => {
    Logging.Debug('renderRejectionReasons', fields);

    // eslint-disable-next-line arrow-body-style
    return fields.map((item: FieldItem) => {
      return (
        <Field
          key={item.index}
          index={item.index}
          active={item.active}
          disabled={!permissionSettingsEdit}
          onChangeActive={onChangeActive}
          label={fieldLabel}
          originalValue={item.originalValue}
          value={item.value}
          onChangeValue={onChangeValue}
          illegalCharacters={[',']}
        />
      );
    });
  };

  // Get the Updated Settings
  const getUpdatedSettings = (): Setting[] => {
    const updatedSettings: Setting[] = [];

    updatedSettings.push({
      name: settingRejectionLetterUrl,
      sequenceNumber: 1,
      activeInd: true,
      value: url,
      dataType: 'STRING',
    });

    fields.forEach((field: FieldItem) => {
      updatedSettings.push({
        name: settingRejectionReason,
        sequenceNumber: field.index,
        activeInd: field.active,
        value: field.value,
        dataType: 'STRING',
      });
    });

    return updatedSettings;
  };

  // Get the Updated Fields
  const getUpdatedFields = (): FieldItem[] => {
    const updatedFields: FieldItem[] = [];

    fields.forEach((field: FieldItem) => {
      updatedFields.push({
        index: field.index,
        active: field.active, // Set the Initial Active to the Current Active
        initialActive: field.active,
        originalValue: field.originalValue,
        value: field.value,
        initialValue: field.value, // Set the Initial Value to the Current Value
      });
    });

    return updatedFields;
  };

  // Make the API Call to Save the Rejection Settings
  const saveRejectionSettings = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);
      const updatedSettings: Setting[] = getUpdatedSettings();

      // Save the Settings
      await api.settings.setCertificateSettingsV4(updatedSettings);

      // Update the Settings Object
      const settingsObject: SettingsContext = updateSettingsObject(settingsContext, 'certificates', updatedSettings);

      // Update the Certicate Settings Context
      setSettingsContext(settingsObject);

      // Update the Fields with the saved Changes
      const updatedFields: FieldItem[] = getUpdatedFields();
      setFields(updatedFields);
      setInitialFields(updatedFields);

      // Update the Initial Values with the Saved Values
      setInitialUrl(url);

      const notificationData: NotificationData = {
        type: 'Information',
        title: 'Rejection Settings Saved',
        description: 'The Rejection Settings were saved.',
        autoClose: true,
        autoCloseTimeOutMilliseconds: 1000,
        position: 'BottomRight',
      };

      setNotification(notificationData);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Rejection Settings Save Error',
        description: 'Unable to Save Rejection Settings.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Save
  const onSave = (): void => {
    // Save
    Logging.Info('Save');

    dispatch(saveRejectionSettings());
  };

  // Cancel
  const onCancel = (): void => {
    // Save
    Logging.Info('Cancel');

    // Reset the Url
    setUrl(initialUrl);

    // Reset the Fields
    setFields(initialFields);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Check if the Values has Changed
  const valuesChanged = (): boolean => {
    let equal = (url === initialUrl);

    fields.forEach((field: FieldItem) => {
      equal = equal && (field.active === field.initialActive) && (field.value === field.initialValue);
    });

    return !equal;
  };

  // Validate the Values
  const validateValues = (): boolean => {
    if (!url) {
      return false;
    }

    if (!url.includes(certificateUuidMarker)) {
      return false;
    }

    if (!url.includes(editTokenMarker)) {
      return false;
    }

    const testUrl = url.replace(certificateUuidMarker, getUuidV4()).replace(editTokenMarker, getUuidV4());
    if (!validateHttpHttpsUri(testUrl)) {
      return false;
    }

    let validValues = true;

    fields.forEach((field: FieldItem) => {
      if (field.active) {
        const validValue = ((field.value !== null) && (field.value !== ''));
        validValues = validValues && validValue;
      }
    });

    return validValues;
  };

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // This Code will run once after the initial Render

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
    };
  }, []);

  const cancelVisible = true;
  const cancelEnabled = valuesChanged();
  const saveVisible = true;
  const saveEnabled = validateValues() && valuesChanged();

  Logging.Debug('Certificate Settings: Rejection Settings');

  const controlValidation: ControlValidation = {
    requiredFields: ['rejection-url'],
    customMessages: [],
  };

  if (url) {
    if (!url.includes(certificateUuidMarker)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'rejection-url', message: `The Url does not contain the ${certificateUuidMarker} marker`, type: 'Error' }];
    }

    if (!url.includes(editTokenMarker)) {
      controlValidation.customMessages = [...controlValidation.customMessages,
        { field: 'rejection-url', message: `The Url does not contain the ${editTokenMarker} marker`, type: 'Error' }];
    }

    // Both Markers are Valid
    if (!controlValidation.customMessages.length) {
      // Replace the Markers with Test Values
      const testUrl = url.replace(certificateUuidMarker, getUuidV4()).replace(editTokenMarker, getUuidV4());
      if (!validateHttpHttpsUri(testUrl)) {
        controlValidation.customMessages = [...controlValidation.customMessages,
          { field: 'rejection-url', message: 'Invalid HTTP / HTTPS Url', type: 'Error' }];
      }
    }
  }

  const urlPlaceHolder = `https://www.mydomain.com/index.html?certificate=${certificateUuidMarker}&token=${editTokenMarker}`;
  const urlTooltip = `Please configure a valid HTTP / HTTPs Url that contain both the ${certificateUuidMarker} and ${editTokenMarker} markers.`;

  controlStateEnabled.disabled = !permissionSettingsEdit;

  return (
    <>
      <VtxTooltip title={urlTooltip}>
        {renderEdit(70, 'rejection-url', 'ECW Rejection Letter Url', 'text', urlPlaceHolder,
          url, setUrl, controlStateEnabled, controlStyle, controlValidation, null)}
      </VtxTooltip>
      <div className="rejectionSettingsTab">
        {renderRejectionReasons()}
      </div>
      <Footer
        onCancel={onCancel}
        cancelEnabled={cancelEnabled}
        cancelVisible={cancelVisible && permissionSettingsEdit}
        saveVisible={saveVisible && permissionSettingsEdit}
        saveEnabled={saveEnabled}
        onSave={onSave}
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default RejectionSettings;
